export type OpsChecklistTemplateId = 'container_haulage_trip' | 'local_delivery_trip';
export type OpsChecklistStepState = 'done' | 'next' | 'open';

export interface OpsChecklistTemplateStep {
  id: string;
  label: string;
  description: string;
  doneFromStatus: string[];
  evidence?: string;
}

export interface OpsChecklistTemplate {
  id: OpsChecklistTemplateId;
  label: string;
  contextLabel: string;
  description: string;
  steps: OpsChecklistTemplateStep[];
}

export interface OpsChecklistRuntimeStep extends OpsChecklistTemplateStep {
  state: OpsChecklistStepState;
}

export interface OpsChecklistRuntime {
  template: OpsChecklistTemplate;
  steps: OpsChecklistRuntimeStep[];
  completed: number;
  total: number;
  percent: number;
  nextStep?: OpsChecklistRuntimeStep;
}

export const OPS_CHECKLIST_TEMPLATES: Record<OpsChecklistTemplateId, OpsChecklistTemplate> = {
  container_haulage_trip: {
    id: 'container_haulage_trip',
    label: 'Container Haulage Trip',
    contextLabel: 'Haulage runbook',
    description: 'Gate, container movement, delivery, POD/EIR evidence and exception discipline.',
    steps: [
      {
        id: 'acknowledge',
        label: 'Acknowledge trip instruction',
        description: 'Confirm job, customer, container, route, time window and special notes.',
        doneFromStatus: ['DISPATCHED', 'EN_ROUTE_PICKUP', 'AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
      },
      {
        id: 'pretrip_check',
        label: 'Check vehicle, chassis and container details',
        description: 'Verify vehicle, chassis, container number, seal and required documents before moving.',
        doneFromStatus: ['EN_ROUTE_PICKUP', 'AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Vehicle / container / document check',
      },
      {
        id: 'pickup_gate',
        label: 'Arrive pickup gate and capture proof',
        description: 'Record depot, port or shipper gate arrival and any waiting or access issue.',
        doneFromStatus: ['AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Gate photo, EIR, time stamp',
      },
      {
        id: 'secure_depart',
        label: 'Secure container and depart',
        description: 'Confirm loading or lift-on is complete, seal is checked, and delivery leg can start.',
        doneFromStatus: ['EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Seal / loading confirmation',
      },
      {
        id: 'delivery_arrival',
        label: 'Arrive delivery or return location',
        description: 'Confirm arrival at consignee, depot or return point and handle access exceptions early.',
        doneFromStatus: ['AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Arrival proof',
      },
      {
        id: 'pod_close',
        label: 'Capture POD/EIR and complete trip',
        description: 'Collect signature, POD, EIR, photos and exception reason before closing.',
        doneFromStatus: ['COMPLETED'],
        evidence: 'POD, EIR, photos, signature',
      },
    ],
  },
  local_delivery_trip: {
    id: 'local_delivery_trip',
    label: 'Local Delivery Trip',
    contextLabel: 'Delivery runbook',
    description: 'Pickup, delivery, receiver proof and exception capture for local transport.',
    steps: [
      {
        id: 'acknowledge',
        label: 'Acknowledge delivery assignment',
        description: 'Confirm pickup, delivery, cargo notes, contact and time window.',
        doneFromStatus: ['DISPATCHED', 'EN_ROUTE_PICKUP', 'AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
      },
      {
        id: 'pickup_ready',
        label: 'Check pickup readiness',
        description: 'Verify goods are ready, packing count is clear, and pickup address is reachable.',
        doneFromStatus: ['EN_ROUTE_PICKUP', 'AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
      },
      {
        id: 'pickup_proof',
        label: 'Arrive pickup and capture proof',
        description: 'Record pickup arrival and proof before departing with cargo.',
        doneFromStatus: ['AT_PICKUP', 'LOADING', 'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Pickup photo / time stamp',
      },
      {
        id: 'delivery_leg',
        label: 'Depart for delivery',
        description: 'Confirm cargo is loaded, route is valid, and delivery ETA is still workable.',
        doneFromStatus: ['EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
      },
      {
        id: 'receiver_arrival',
        label: 'Arrive receiver and confirm handover',
        description: 'Confirm receiver, unloading access and any short/damage issue.',
        doneFromStatus: ['AT_DELIVERY', 'UNLOADING', 'COMPLETED'],
        evidence: 'Receiver arrival proof',
      },
      {
        id: 'pod_close',
        label: 'Capture POD and complete trip',
        description: 'Collect signature, photos, result code and notes before closing.',
        doneFromStatus: ['COMPLETED'],
        evidence: 'POD, signature, photos',
      },
    ],
  },
};

export function resolveTripChecklistTemplate(tripType?: string | null, serviceMode?: string | null): OpsChecklistTemplate {
  const normalized = `${tripType ?? ''} ${serviceMode ?? ''}`.toUpperCase();
  if (normalized.includes('FCL') || normalized.includes('CONTAINER')) {
    return OPS_CHECKLIST_TEMPLATES.container_haulage_trip;
  }
  return OPS_CHECKLIST_TEMPLATES.local_delivery_trip;
}

export function buildOpsChecklistRuntime(template: OpsChecklistTemplate, currentStatus: string): OpsChecklistRuntime {
  let nextAssigned = false;
  const normalizedStatus = currentStatus.toUpperCase();
  const steps = template.steps.map((step) => {
    const done = step.doneFromStatus.includes(normalizedStatus);
    const state: OpsChecklistStepState = done ? 'done' : nextAssigned ? 'open' : 'next';
    if (!done && !nextAssigned) nextAssigned = true;
    return { ...step, state };
  });
  const completed = steps.filter((step) => step.state === 'done').length;
  const total = steps.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return {
    template,
    steps,
    completed,
    total,
    percent,
    nextStep: steps.find((step) => step.state === 'next'),
  };
}
